import { useMsal } from "@azure/msal-react";
import axios from "axios";
import {
    ReactElement,
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";
// import { useNavigate } from "react-router-dom";
import { msalConfig } from "../authConfig";
import z from "zod";

const appRoleAssignmentsSchema = z.object({
    value: z.array(
        z.object({
            appRoleId: z.string(),
            resourceId: z.string(),
        }),
    ),
});

const servicePrincipalSchema = z.object({
    appRoles: z.array(
        z.object({
            id: z.string(),
            // value: "AllQueues_Admin",
            value: z.string(),
        }),
    ),
});

export const ROLES = {
    "Location.Writer": "Location.Writer",
    "Team.Writer": "Team.Writer",
    "ServiceFailure.CSM": "ServiceFailure.CSM",
    "ServiceFailure.OpsMgmt": "ServiceFailure.OpsMgmt",
    "ServiceFailure.Standard": "ServiceFailure.Standard",
    "TrackingPartnerMgmt.Writer": "TrackingPartnerMgmt.Writer",
    "TrackingPartnerMgmt.Reader": "TrackingPartnerMgmt.Reader",
    "OrderInvoicing.Write": "OrderInvoicing.Write",
    "BidRateCalculator.Write": "BidRateCalculator.Write",
    "BidRateCalculator.Admin": "BidRateCalculator.Admin",
    "DriverRoadTest.ReadWrite": "DriverRoadTest.ReadWrite",
    "PFSTabletInventory.ReadWrite": "PFSTabletInventory.ReadWrite",
    "HRConfigMgt.Tasker": "HRConfigMgt.Tasker",
    "HRConfigMgt.Engauge": "HRConfigMgt.Engauge",
    "HRConfigMgt.EngaugeBeta": "HRConfigMgt.EngaugeBeta",
    "MVTECenter.HelpdeskAdmin": "MVTECenter.HelpdeskAdmin",
    "CollectionsDashboard.ReadWrite": "CollectionsDashboard.ReadWrite",
    "Bids.ReadWrite": "Bids.ReadWrite",
    "ITAdmin.SteelTool.Write": "ITAdmin.SteelTool.Write",
    "ITAdmin.TaskerTool.Write": "ITAdmin.TaskerTool.Write"
} as const;

export const ROLE_GUIDS = 
{
    "Location.Writer": "03fb481b-7ac5-4b35-82a6-d600c18c0b88",
    "Team.Writer": "686c552d-e0ea-47f6-87c6-5c5947c1507a",
    "ServiceFailure.CSM": "45db2e4c-ebe3-40af-a3cf-aba53c284f28",
    "ServiceFailure.OpsMgmt": "7c864c80-4039-478f-a5bf-3920ce838bc4",
    "ServiceFailure.Standard": "57b83965-8ba4-4b33-8e53-a056e6d4820c",
    "TrackingPartnerMgmt.Writer": "9d4aa03e-69ff-4472-acbf-5bcd84ddb0da",
    "TrackingPartnerMgmt.Reader": "a1c42580-f03e-411c-acd7-efe5d546430a",
    "OrderInvoicing.Write": "1a0a3ca8-3643-4d1a-8e83-1697089e7ce9",
    "BidRateCalculator.Write": "9544f64b-b002-49eb-b9e7-f88d46cbedd8",
    "BidRateCalculator.Admin": "f9fe78d9-cbe3-4d98-ad0b-0c265d81348c",
    "DriverRoadTest.ReadWrite": "31658862-0470-4aba-b8bb-8ebc48b05833",
    "PFSTabletInventory.ReadWrite": "c3a5b481-bdaa-4cb6-9aad-3a04f52006ed",
    "HRConfigMgt.Tasker": "eb2c9269-6cf6-4c2b-ac7b-86d7a2dfbc03",
    "HRConfigMgt.Engauge": "176425e3-8243-42e9-9a42-e5622a9644c3",
    "HRConfigMgt.EngaugeBeta": "14354004-6104-49e9-9086-fec95ae97b6b",
    "CollectionsDashboard.ReadWrite": "621a2b2c-d75d-48fe-8672-4940e6ca88e4",
    "MVTECenter.HelpdeskAdmin": "c77f720f-47e3-460e-9d96-53d46323b63c",
    "Bids.ReadWrite": "1",
    "ITAdmin.SteelTool.Write": "b64389c8-5d20-4fce-89b8-b08f0e61a5e3",
    "ITAdmin.TaskerTool.Write": "9136bb03-27d6-48f5-839a-e2ed18446f6e"  
} as const;

type ObjectValues<T> = T[keyof T];
export type Roles = ObjectValues<typeof ROLES>;
const RolesContext = createContext<{
    roles: Set<Roles>;
    checkHasRole: (role: Roles) => boolean;
} | null>(null);

export function RolesContextProvider({ children }: { children: ReactElement }) {
    const [isRolesLoading, setRolesLoading] = useState(true);
    const [roles, setRoles] = useState<Set<Roles>>(new Set());
    // const navigate = useNavigate();
    const { accounts, instance } = useMsal();
    const account = accounts[0];

    useEffect(() => {
        async function someAsync() {
            const aquireTokenSilentResult = await instance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account,
                extraQueryParameters: {
                    claims: JSON.stringify({ id_token: { roles: null } }),
                },
            });

            // Fetch the user's appRoleAssignments
            const appRoleAssignmentsResponse = await axios.get(
                "https://graph.microsoft.com/v1.0/me/appRoleAssignments",
                {
                    headers: {
                        Authorization: `Bearer ${aquireTokenSilentResult.accessToken}`,
                    },
                },
            );

            const appRoleAssignments = appRoleAssignmentsSchema.parse(
                appRoleAssignmentsResponse.data,
            );

            // Fetch the appRoles defined in your app registration's service principal
            const servicePrincipalResponse = await axios.get(
                `https://graph.microsoft.com/v1.0/servicePrincipals/${msalConfig.auth.enterpriseObjectId}`,
                {
                    headers: {
                        Authorization: `Bearer ${aquireTokenSilentResult.accessToken}`,
                    },
                },
            );

            const servicePrincipal = servicePrincipalSchema.parse(
                servicePrincipalResponse.data,
            );

            // Create a mapping of appRoleId to role name
            const appRolesMap = servicePrincipal.appRoles.reduce(
                (acc, role) => {
                    acc[role.id] = role.value;
                    return acc;
                },
                {} as Record<string, string>,
            );

            // Extract the role names for the user's appRoleAssignments
            const userRoles = appRoleAssignments.value
                .filter(
                    (assignment) =>
                        assignment.resourceId ===
                        msalConfig.auth.enterpriseObjectId,
                )
                .map((assignment) => appRolesMap[assignment.appRoleId]);

            const filteredRoles = userRoles.filter((role): role is Roles =>
                Object.values(ROLES).includes(role as Roles),
            );

            if (filteredRoles.length === 0) {
                // navigate("/no-permissions");
                // window.location.href = "/no-permissions";
                return;
            }

            setRoles(new Set(filteredRoles));
            setRolesLoading(false);
        }

        someAsync();
    }, [account, instance]);

    function checkHasRole(role: Roles) {
        return roles.has(role);
    }

    return (
        <RolesContext.Provider value={{ roles, checkHasRole }}>
            {!isRolesLoading ? children : null}
        </RolesContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRoles() {
    const context = useContext(RolesContext);

    if (context === null) {
        throw new Error("useRoles must be used within a RolesContextProvider");
    }

    return context;
}
